import React, { useState, useEffect, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { es } from 'date-fns/locale';
import CalendarPresets from '../calendar/CalendarPresets';

registerLocale('es', es);

const CustomDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <button
    type="button"
    className="sf-date-input"
    onClick={onClick}
    ref={ref}
  >
    {value || placeholder}
  </button>
));
CustomDateInput.displayName = 'CustomDateInput';

const TwoMonthContainer = ({ className, children }) => (
  <div className={className} style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
    {children}
  </div>
);

const MobileCalendarContainer = ({ className, children }) => (
  <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
    {children}
  </div>
);

const DateField = ({
  dateType,
  startDate,
  endDate,
  onChangeRange,
  onChangeSingle,
  minDate,
  maxDays = 365,
  isOpen,
  onOpen,
  onClose,
  validationError,
  inline,
  monthsShown,
  onLoadMoreMonths,
  isMobile
}) => {
  const months = monthsShown || 2;
  const [showLoadMore, setShowLoadMore] = useState(false);
  const calendarRef = useRef(null);

  // Detect scroll to show "Load more" button when reaching end
  useEffect(() => {
    if (!isMobile || !calendarRef.current) return;

    const calendar = calendarRef.current.querySelector('.sf-calendar');
    if (!calendar) return;

    // En fullscreen el body padre es el que scrollea, no el calendario
    const scrollContainer =
      calendar.closest('.sf-mobile-sheet__body--fullscreen') || calendar;

    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setShowLoadMore(true);
      } else {
        setShowLoadMore(false);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  const handlePreset = (days) => {
    const start = new Date(minDate);
    const end = new Date(minDate);
    end.setDate(end.getDate() + days - 1);
    if (dateType === 'range') {
      onChangeRange([start, end]);
    } else {
      onChangeSingle(start);
    }
  };

  const showPresets = dateType === 'range';

  if (dateType === 'range') {
    // Calculate maxDate: startDate + (maxDays - 1) days (maxDays inclusive)
    let maxDate = null;
    if (startDate) {
      maxDate = new Date(startDate);
      maxDate.setDate(maxDate.getDate() + maxDays - 1);
    }

    // Calculate day count: endDate - startDate + 1 (inclusive)
    let dayCount = null;
    if (startDate && endDate) {
      const diffTime = endDate - startDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      dayCount = diffDays + 1; // +1 to include the first day
    }

    return (
      <div ref={calendarRef} className={`sf-date-field ${validationError ? 'sf-field-error' : ''}`} style={{ position: 'relative' }}>
        {dayCount && (
          <div className="sf-day-counter">
            {dayCount} {dayCount === 1 ? 'día' : 'días'}
          </div>
        )}
        {showPresets && (
          <CalendarPresets
            onSelect={handlePreset}
            minDate={minDate}
            maxDays={maxDays}
            activePreset={null}
          />
        )}
        <DatePicker
          key={months}
          selected={startDate}
          onChange={(dates) => onChangeRange(dates)}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          monthsShown={months}
          openToDate={minDate}
          minDate={minDate}
          maxDate={maxDate}
          placeholderText="Seleccionar fechas"
          dateFormat="dd-MMM-yyyy"
          locale="es"
          showPopperArrow={false}
          formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 1)}
          calendarClassName={`sf-calendar${months > 1 ? ' sf-calendar--two-months' : ''}`}
          popperClassName="sf-datepicker-popper"
          calendarContainer={isMobile ? MobileCalendarContainer : (months > 1 ? TwoMonthContainer : undefined)}
          inline={inline}
          open={inline ? undefined : isOpen}
          onCalendarOpen={onOpen}
          onCalendarClose={onClose}
          onClickOutside={onClose}
          onInputClick={onOpen}
          customInput={inline ? undefined : <CustomDateInput />}
          onChangeRaw={(e) => e.preventDefault()}
        />
        {isMobile && onLoadMoreMonths && showLoadMore && (
          <button
            type="button"
            className="sf-load-more-dates"
            onClick={onLoadMoreMonths}
          >
            Carga más fechas
          </button>
        )}
      </div>
    );
  }

  if (dateType === 'date') {
    return (
      <div className={`sf-date-field ${validationError ? 'sf-field-error' : ''}`} style={{ position: 'relative' }}>
        {startDate && (
          <div className="sf-day-counter">
            365 días
          </div>
        )}
        <DatePicker
          selected={startDate}
          onChange={(date) => onChangeSingle(date)}
          minDate={minDate}
          locale="es"
          placeholderText="Seleccionar fecha"
          dateFormat="dd-MMM-yyyy"
          showPopperArrow={false}
          calendarClassName="sf-calendar"
          popperClassName="sf-datepicker-popper"
          inline={inline}
          open={inline ? undefined : isOpen}
          onCalendarOpen={onOpen}
          onCalendarClose={onClose}
          onClickOutside={onClose}
          onInputClick={onOpen}
          customInput={inline ? undefined : <CustomDateInput />}
          onChangeRaw={(e) => e.preventDefault()}
        />
      </div>
    );
  }

  // single date
  return (
    <div className={`sf-date-field ${validationError ? 'sf-field-error' : ''}`}>
      <DatePicker
        selected={startDate}
        onChange={(date) => onChangeSingle(date)}
        minDate={minDate}
        locale="es"
        placeholderText="Seleccionar fecha"
        dateFormat="dd-MMM-yyyy"
        showPopperArrow={false}
        calendarClassName="sf-calendar"
        popperClassName="sf-datepicker-popper"
        inline={inline}
        open={inline ? undefined : isOpen}
        onCalendarOpen={onOpen}
        onCalendarClose={onClose}
        onClickOutside={onClose}
        onInputClick={onOpen}
        customInput={inline ? undefined : <CustomDateInput />}
        onChangeRaw={(e) => e.preventDefault()}
      />
    </div>
  );
};

export default DateField;
